import { HttpClient } from '@angular/common/http';
import { inject, signal } from '@angular/core';
import { OpenQuoteRequestsItem } from '../types/quoterequest.types';
import {
  AccountTaskSetsListResponse,
  AdminTaskSetsListResponse,
  TaskSet,
  TaskSetResponse,
  TaskSetsListItem,
  TaskSetsListResponse,
} from '../types/task-viewer/taskset.types';
import { TaskMessageQueue } from '../types/task-viewer/tasks-message.types';
import { map, Observable, of } from 'rxjs';
import { environment } from '@sbd-doug-frontend/app/environments/environment';
import {
  Task,
  TaskResponse,
} from '@sbd-doug-frontend/shared/lib/types/task-viewer/tasks.types';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';

export abstract class SharedBaseTaskViewerService {
  _httpClient = inject(HttpClient);
  serviceName = signal<string>('');
  isCreatingNewTask = signal<boolean>(false);
  isComposingMessage = signal<boolean>(false);
  taskInEdition = signal<boolean>(false);
  taskInEditionState = signal<number>(0);
  displayNewMessagePlaceholder = signal<boolean>(false);
  displayNewTaskPlaceholder = signal<boolean>(false);
  showTaskEditor = signal<boolean>(false);
  selectedTaskSetFromTaskSetList = signal<TaskSet>(null);
  taskSets = signal<TaskSetsListItem[]>([]);
  selectedQR = signal<OpenQuoteRequestsItem>(null);
  showMessageEditor = signal<boolean>(false);
  taskSet = signal<TaskSet>(null);
  taskMessage = signal<TaskMessageQueue>(null);
  newTask = signal<Task>(null);
  mostRecentAppliedPreparedFilter = signal<string>('');
  newTaskStoredState = signal<any>(null);
  isLoading = signal<boolean>(false);
  aNewTaskCreated = signal<boolean>(false);
  newTasksetCreated = signal<TaskSet>(null);
  _cathyService = inject(CathyService);

  scope = signal<string>('');
  accountId = signal<number>(0);

  getMyTasks(filters: any = {}): Observable<TaskSetsListItem[] | null> {
    return of(null);
  }

  getMyTasksets(filters: any = {}): Observable<TaskSetsListItem[]> {
    const params = filters || {};
    this.isLoading.set(true);

    return this._httpClient
      .get<AdminTaskSetsListResponse>(
        `${environment.apiBaseUrl}/api/v4/tasksets/admin/my-tasksets`, {
          params: params,
        },
      ).pipe(
        map((response) => {
          this.isLoading.set(false);
          this.taskSets.set(response.data.admin.tasksets);
          return response.data.admin.tasksets;
        }),
      );

  }

  getTaskSet(taskSetID: number): Observable<TaskSet> {
    return this._httpClient
      .get<TaskSetResponse>(
        `${environment.apiBaseUrl}/api/v4/tasksets/${taskSetID}`,
      )
      .pipe(
        map((response) => {
          const currentTaskSet = response.data.taskset.taskset;
          if (currentTaskSet.tasks) {
            currentTaskSet.tasks.sort((a, b) => {
              const aCancelled = !!a.task_cancellation_date;
              const bCancelled = !!b.task_cancellation_date;

              if (aCancelled !== bCancelled) {
                return aCancelled ? 1 : -1;
              }

              const aCompleted = !!a.task_completion_date;
              const bCompleted = !!b.task_completion_date;

              if (aCompleted !== bCompleted) {
                return aCompleted ? 1 : -1;
              }

              // Then sort by due date
              if (!a.task_due_date && !b.task_due_date) return 0;
              if (!a.task_due_date) return 1;
              if (!b.task_due_date) return -1;

              const dateA = new Date(a.task_due_date).getTime();
              const dateB = new Date(b.task_due_date).getTime();
              return dateB - dateA;
            });
          }


          this.selectedTaskSetFromTaskSetList.set(currentTaskSet);
          return currentTaskSet;
        }),
      );
  }

  //Even if assigned to an admin user
  getAdminFilteredDataPerCriteria(criteria: string, filters: any = {}): Observable<TaskSetsListItem[]> {
    const params = filters || {};
    this.mostRecentAppliedPreparedFilter.set(criteria);
    this.isLoading.set(true);
    return this._httpClient
      .get<AdminTaskSetsListResponse>(
        `${environment.apiBaseUrl}/api/v4/tasks/admin/filter-tasks/${criteria}`, {
          params: params,
        },
      ).pipe(
        map((response) => {
          this.isLoading.set(false);
          this.taskSets.set(response.data.admin.tasksets);
          this.selectedTaskSetFromTaskSetList.set(null);
          return response.data.admin.tasksets;
        }),
      );
  }

  /**
   * Refreshes the tasksets list based on the most recent applied filter or default fetch.
   */
  refreshTaskSets(isNewTask : boolean = false): void {

    if(isNewTask){
      this.selectedTaskSetFromTaskSetList.set(this.newTasksetCreated())
      this.getTaskSet(this.newTasksetCreated().task_set_id).subscribe()
      this.newTasksetCreated.set(null)
      return;
    }


    const currentTaskset = this.selectedTaskSetFromTaskSetList();
    const currentFilter = this.mostRecentAppliedPreparedFilter();

    const handleResponse = (t: TaskSetsListItem[]) => {
      const f = t.filter(taskset => taskset.task_set_id === currentTaskset?.task_set_id);
      if (f.length > 0) {
        this.selectedTaskSetFromTaskSetList.set(f[0]);
        this.getTaskSet(f[0].task_set_id).subscribe();
      } else {
        this.selectedTaskSetFromTaskSetList.set(null);
      }
    };

    if (this.scope() === 'my_tasks') {
      if (currentFilter) {
        this.getAdminFilteredDataPerCriteria(currentFilter).subscribe(handleResponse);
      } else {
        this.getMyTasksets({ 'quick_filter[eq]': 'assigned_to_me' }).subscribe(handleResponse);
      }
    } else if (this.scope() === 'my_teams_task') {
      if (currentFilter) {
        this.getAdminFilteredDataPerCriteria(currentFilter).subscribe(handleResponse);
      } else {
        this.getMyTasksets({ 'quick_filter[eq]': 'my_teams_task' }).subscribe(handleResponse);
      }
    } else if (this.scope() === 'filter_my_tasks') {
      if (currentFilter) {
        this.getAdminFilteredDataPerCriteria(currentFilter).subscribe(handleResponse);
      }
    } else if (this.scope() === 'account_tasks') {
      this.getAccountTasksets(this.accountId()).subscribe(handleResponse);
    }
  }

  sendMessageToAccountUser(data: any, accountUserId: number, accountId = 0, loadTasksets = true): Observable<TaskSetResponse> {
    this.isLoading.set(true);
    return this._httpClient
      .post<TaskSetResponse>(
        `${environment.apiBaseUrl}/api/v4/accounts/user/${accountUserId}/tasksets/task/send-message`,
        data,
      ).pipe(
        map((response) => {
          this.isLoading.set(false);
          if (loadTasksets) {
            this.refreshTaskSets();
          }

          return response;
        }),
      );
  }


  getAccountTasksets(accountId: number, filters: any = {}): Observable<TaskSetsListItem[]> {
    const params = filters || {};
    this.isLoading.set(true);

    return this._httpClient
      .get<AccountTaskSetsListResponse>(
        `${environment.apiBaseUrl}/api/v4/tasksets/account/${accountId}`, {
          params: params,
        },
      ).pipe(
        map((response) => {
          this.isLoading.set(false);
          this.taskSets.set(response.data.account.tasksets);
          return response.data.account.tasksets;
        }),
      );

  }

  /*TASKSET HELPERS - API CALLS*/


  copyObjectPartIntoTask(task: Task, objectPart: any) {
    const updatedTask = {
      ...task,
      ...objectPart,
    };
    this.replaceTaskFromTaskSet(updatedTask);
  }

  addTaskToTaskSet(task: Task) {
    const currentTaskSet = this.selectedTaskSetFromTaskSetList();
    if (currentTaskSet) {
      const updatedTasks = currentTaskSet.tasks ? [...currentTaskSet.tasks, task] : [task];

      // Sort tasks by task_due_date (earliest first)
      updatedTasks.sort((a, b) => {
        const dateA = new Date(a.task_due_date).getTime();
        const dateB = new Date(b.task_due_date).getTime();
        return dateA - dateB;
      });

      this.selectedTaskSetFromTaskSetList.set({
        ...currentTaskSet,
        tasks: updatedTasks,
      });
    }
  }

  replaceTaskFromTaskSet(task: Task) {
    const currentTaskSet = this.selectedTaskSetFromTaskSetList();
    if (currentTaskSet && currentTaskSet.tasks) {
      const taskIndex = currentTaskSet.tasks.findIndex(t => t.task_id === task.task_id);
      if (taskIndex !== -1) {
        const updatedTasks = [...currentTaskSet.tasks];
        updatedTasks[taskIndex] = task;
        this.selectedTaskSetFromTaskSetList.set({
          ...currentTaskSet,
          tasks: updatedTasks,
        });
      }
    }
  }

  /**
   * Adds a task set to the current list of task sets
   * @param taskSet - The TaskSet object to be added to the list
   */
  addTaskSetToTaskSets(taskSet: TaskSet) {
    const currentTaskSets = this.taskSets();
    this.taskSets.set([...currentTaskSets, taskSet]);
  }

  replaceOrAddTaskSetInTaskSets(updatedTaskSet: TaskSet, addIfNotFound = false) {
    const currentTaskSets = this.taskSets();
    const existingIndex = currentTaskSets.findIndex(taskSet =>
      taskSet.task_set_id === updatedTaskSet.task_set_id,
    );

    if (existingIndex !== -1) {
      const updatedTaskSets = [...currentTaskSets];
      updatedTaskSets[existingIndex] = updatedTaskSet;
      this.taskSets.set(updatedTaskSets);
    } else if (addIfNotFound) {
      this.addTaskSetToTaskSets(updatedTaskSet);
    }
  }

  createTask(task: Task): Observable<TaskResponse> {
    return this._httpClient.post<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/create-task-for-entity`, task).pipe(
      map(
        (response) => {
          if (!response.success) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          if (response.data.taskset?.taskset?.task_set_id) {
            this.addTaskSetToTaskSets(response.data.taskset.taskset);
            this.newTasksetCreated.set(response.data.taskset.taskset);
          } else {
            this.addTaskToTaskSet(response.data.task.task);
          }
          this.refreshTaskSets(true);
          return response;
        },
      ),
    );
  }

  completeTask(task: Task): Observable<Task> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task.task_id}/complete-task`, task).pipe(
      map(
        (response) => {
          if (!response.success) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._cathyService.success(response.message);
          this.replaceTaskFromTaskSet(response.data.task.task);
          this.refreshTaskSets();
          return response.data.task.task;
        },
      ),
    );
  }

  reOpenTask(taskId: number): Observable<Task> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${taskId}/reopen-task`, {}).pipe(
      map(
        (response) => {
          if (!response.success) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          this._cathyService.success(response.message);
          this.replaceTaskFromTaskSet(response.data.task.task);
          this.refreshTaskSets();
          return response.data.task.task;
        },
      ),
    );
  }

  cancelTask(task: Task): Observable<Task> {
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task.task_id}/cancel-task`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          const updatedTask = response.data.task.task;
          this.replaceTaskFromTaskSet(updatedTask);
          this.refreshTaskSets();
          this._cathyService.success(response.message);
          return response.data.task.task;
        },
      ),
    );
  }

  updateTask(task: Task | FormData): Observable<Task> {
    const taskId = task instanceof FormData ? task.get('task_id') : task.task_id;
    return this._httpClient.put<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${taskId}`, task).pipe(
      map(
        (response) => {
          if (response.success === false) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }
          // const t = response.data.task.task;
          // this.replaceTaskFromTaskSet(t);
          //
          // if (response.data.taskset?.taskset) {
          //   this.replaceOrAddTaskSetInTaskSets(response.data.taskset?.taskset, true);
          // }
          this.refreshTaskSets();
          this._cathyService.success(response.message);
          return response.data.task.task;
        },
      ),
    );
  }


  disassociateQuoteRequestFromTask(task_id: number, quote_request_id: number | undefined): Observable<TaskResponse | null> {
    if (quote_request_id === undefined) return of(null);
    return this._httpClient.get<TaskResponse>(`${environment.apiBaseUrl}/api/v4/task/${task_id}/disassociate-qr/${quote_request_id}`).pipe(
      map(
        (response) => {
          if (!response.success) {
            this._cathyService.error(response.message);
            throw new Error(response.message);
          }

          const t = response.data.task.task;
          this.replaceTaskFromTaskSet(t);
          this.refreshTaskSets();

          return response;
        },
      ),
    );
  }

  markAccountUserMessageAsRead(task: Task, accountUserId: number): Observable<any> {
    return this._httpClient.get<any>(
      `${environment.apiBaseUrl}/api/v4/accounts/user/mark-as-read/${task.task_id}`,
    ).pipe(
      map((response) => {
        if (response && response?.success) {
          this.refreshTaskSets();
          if (task?.task_set_id) {
            this.getTaskSet(task.task_set_id).subscribe();
          }
        }
        return response;
      }),
    );
  }

  markAccountUserMessageAsUnread(task: Task, accountUserId: number): Observable<any> {
    return this._httpClient.get<any>(
      `${environment.apiBaseUrl}/api/v4/accounts/user/mark-as-unread/${task.task_id}`,
    ).pipe(
      map((response) => {
        if (response && response?.success) {
          this.refreshTaskSets();
          if (task?.task_set_id) {
            this.getTaskSet(task.task_set_id).subscribe();
          }
        }
        return response;
      }),
    );
  }

}
